/**
 * Communications API Service
 * Handles bulk SMS, WhatsApp, Voice AI, and Email messaging
 */

import { apiClient, handleApiError } from './apiClient';

// ============================================================================
// TYPES
// ============================================================================

export interface BulkSMSRequest {
  group_id: string;
  message: string;
  /** Values for the template's `input` variables (BE #1220). */
  variable_inputs?: Record<string, string>;
  translate_by_preference?: boolean;
  default_language?: string;
}

export interface BulkWhatsAppRequest {
  group_id: string;
  message: string;
  /** Values for the template's `input` variables (BE #1220). */
  variable_inputs?: Record<string, string>;
  media_urls?: string[];
  translate_by_preference?: boolean;
  default_language?: string;
}

export interface BulkVoiceRequest {
  group_id: string;
  voice_script: string;
  /** Values for the template's `input` variables (BE #1220). */
  variable_inputs?: Record<string, string>;
  voice_config?: {
    voice?: string;
    language?: string;
    interactive?: boolean;
  };
  translate_by_preference?: boolean;
}

export interface BulkEmailRequest {
  group_id: string;
  subject: string;
  html_content: string;
  /** Values for the template's `input` variables (BE #1220). */
  variable_inputs?: Record<string, string>;
  from_email?: string;
  from_name?: string;
  translate_by_preference?: boolean;
  default_language?: string;
}

export interface BulkSocialMediaRequest {
  organization_id: number;
  platforms: string[];  // ['facebook', 'twitter', 'linkedin', etc.]
  content: string;
  hashtags?: string;
  media_urls?: string[];
  scheduled_at?: string;  // ISO format for scheduling
}

export interface BulkSocialMediaResponse {
  task_id: string;
  status: string;
  message: string;
  platforms: string[];
  results?: {
    success: Array<{ platform: string; account: string; post_id?: string; url?: string }>;
    failed: Array<{ platform: string; account: string; error: string }>;
  };
}

export interface BulkCommunication {
  id: string;
  group_id: string;
  message_template: string;
  channel: 'sms' | 'whatsapp' | 'voice' | 'email';
  status: 'processing' | 'completed' | 'failed' | 'cancelled';
  total_members: number;
  sent_count: number;
  failed_count: number;
  pending_count: number;
  progress_percentage: number;
  total_cost_cents: number;
  total_cost_dollars: number;
  created_at: string;
  completed_at?: string;
}

export interface MessageLog {
  id: string;
  member_id: string;
  phone_number?: string;
  email?: string;
  message_content: string;
  translated_message?: string;
  target_language?: string;
  status: 'sent' | 'delivered' | 'failed' | 'pending';
  message_sid?: string;
  sms_segments?: number;
  total_cost_cents: number;
  error_message?: string;
  sent_at?: string;
  created_at: string;
}

export interface BulkCommunicationHistory {
  id: string;
  group_id: string;
  group_name: string;
  message_template: string;
  channel: 'sms' | 'whatsapp' | 'voice' | 'email';
  status: string;
  total_members: number;
  sent_count: number;
  failed_count: number;
  total_cost_dollars: number;
  created_by_name: string;
  created_at: string;
  completed_at?: string;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

export const communicationsApi = {
  /**
   * Send bulk SMS to group
   */
  async sendBulkSMS(request: BulkSMSRequest): Promise<{ task_id: string; status: string; message: string }> {
    try {
      const response = await apiClient.post('/api/bulk-communications/send-sms', request);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Send bulk WhatsApp to group
   */
  async sendBulkWhatsApp(request: BulkWhatsAppRequest): Promise<{ task_id: string; status: string; message: string }> {
    try {
      const response = await apiClient.post('/api/bulk-communications/send-whatsapp', request);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Send bulk voice AI calls to group
   */
  async sendBulkVoice(request: BulkVoiceRequest): Promise<{ task_id: string; status: string; message: string }> {
    try {
      const response = await apiClient.post('/api/bulk-communications/send-voice', request);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Send bulk email to group
   */
  async sendBulkEmail(request: BulkEmailRequest): Promise<{ task_id: string; status: string; message: string }> {
    try {
      const response = await apiClient.post('/api/bulk-communications/send-email', request);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Send a single test message to the logged-in user (no member targeted).
   * channel: 'sms' | 'whatsapp' | 'email' | 'voice'. Optional `to` overrides the recipient.
   */
  async testSendToSelf(request: {
    channel: 'sms' | 'whatsapp' | 'email' | 'voice';
    to?: string;
    message?: string;
  }): Promise<{ channel: string; status: string; detail: string; external_id?: string; to?: string }> {
    try {
      const response = await apiClient.post('/api/bulk-communications/test-send', request);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get bulk communication status
   */
  async getBulkCommunicationStatus(bulkCommunicationId: string): Promise<BulkCommunication> {
    try {
      const response = await apiClient.get(`/api/bulk-communications/${bulkCommunicationId}/status`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get individual message logs for bulk communication
   */
  async getMessageLogs(
    bulkCommunicationId: string,
    params?: {
      status_filter?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<MessageLog[]> {
    try {
      const response = await apiClient.get(`/api/bulk-communications/${bulkCommunicationId}/messages`, { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get bulk communication history
   */
  async getBulkCommunicationHistory(params?: {
    organization_id?: number;
    group_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<BulkCommunicationHistory[]> {
    try {
      const response = await apiClient.get('/api/bulk-communications/history', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Cancel pending bulk communication
   */
  async cancelBulkCommunication(bulkCommunicationId: string): Promise<void> {
    try {
      await apiClient.delete(`/api/bulk-communications/${bulkCommunicationId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Post to social media platforms
   */
  async sendBulkSocialMedia(request: BulkSocialMediaRequest): Promise<BulkSocialMediaResponse> {
    try {
      const response = await apiClient.post('/api/bulk-communications/send-social', request);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
};
